import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Divider,
  Link,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  VisibilityOutlined,
  VisibilityOffOutlined,
  BusinessOutlined,
  EmailOutlined,
  LockOutlined,
  LoginOutlined,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { tritonStyles } from '../styles/TritonTheme';

// Styled Components
const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #1a365d 0%, #2d3748 50%, #4a5568 100%)',
  padding: theme.spacing(3),
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    opacity: 0.1,
  },
}));

const LoginCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 480,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(3),
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0px 8px 32px rgba(26, 54, 93, 0.25), 0px 0px 0px 1px rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  position: 'relative',
  zIndex: 1,
}));

const LoginHeader = styled(Box)(({ theme }) => ({
  background: tritonStyles.gradients.primary,
  color: '#ffffff',
  padding: theme.spacing(4, 4, 3, 4),
  textAlign: 'center',
  position: 'relative',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 1,
    background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backgroundColor: 'rgba(247, 250, 252, 0.8)',
    transition: tritonStyles.transitions.standard,
    
    '& fieldset': {
      borderColor: '#e2e8f0',
      borderWidth: '2px',
    },
    
    '&:hover fieldset': {
      borderColor: '#cbd5e0',
    },
    
    '&.Mui-focused fieldset': {
      borderColor: '#1a365d',
      borderWidth: '2px',
    },
    
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      boxShadow: '0px 4px 12px rgba(26, 54, 93, 0.1)',
    },
  },
  
  '& .MuiInputLabel-root': {
    color: '#4a5568',
    fontWeight: 500,
    
    '&.Mui-focused': {
      color: '#1a365d',
    },
  },
}));

const LoginButton = styled(Button)(({ theme }) => ({
  height: 56,
  borderRadius: theme.spacing(1.5),
  background: tritonStyles.gradients.primary,
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
  transition: tritonStyles.transitions.standard,
  
  '&:hover': {
    background: tritonStyles.gradients.secondary,
    boxShadow: '0px 4px 16px rgba(26, 54, 93, 0.25)',
    transform: 'translateY(-2px)',
  },
  
  '&:disabled': {
    background: '#a0aec0',
    color: '#ffffff',
  },
}));

const TritonLogin = ({ setUser }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError(''); // Clear error when user starts typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await apiService.post('/api/auth/login', formData);

      if (data.success) {
        console.log('✅ Session-Login erfolgreich:', data);
        // Kein localStorage mehr - Session wird automatisch verwaltet
        await setUser(data);
      } else {
        setError(data.message || 'Anmeldung fehlgeschlagen');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Verbindungsfehler. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LoginHeader>
          <Avatar
            sx={{
              width: 72,
              height: 72,
              margin: '0 auto 16px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <BusinessOutlined sx={{ fontSize: 36, color: '#d69e2e' }} />
          </Avatar>
          
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Willkommen zurück
          </Typography>
          
          <Typography variant="body1" sx={{ opacity: 0.9, fontSize: '1.1rem' }}>
            Hausverwaltung Login - Triton System
          </Typography>
        </LoginHeader>

        <CardContent sx={{ p: 4 }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                backgroundColor: 'rgba(229, 62, 62, 0.1)',
                border: '1px solid rgba(229, 62, 62, 0.2)',
                '& .MuiAlert-icon': {
                  color: '#e53e3e',
                },
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <StyledTextField
              fullWidth
              label="E-Mail-Adresse"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="username"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: '#4a5568' }} />
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              fullWidth
              label="Passwort"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: '#4a5568' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      sx={{ color: '#4a5568' }}
                    >
                      {showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <LoginButton
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginOutlined />}
              sx={{ mb: 3 }}
            >
              {loading ? 'Anmeldung läuft...' : 'Anmelden'}
            </LoginButton>

            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/verkaeufer-login')}
              sx={{
                mb: 3,
                height: 52,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#1a365d',
                color: '#1a365d',
              }}
            >
              Zur Verkaufs- / Mietverwaltung
            </Button>

            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" sx={{ color: '#718096', px: 2 }}>
                oder
              </Typography>
            </Divider>

            <Box sx={{ textAlign: 'center' }}>
              <Link
                href="/register"
                sx={{
                  color: '#1a365d',
                  textDecoration: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#0f2027',
                  },
                }}
              >
                Noch kein Konto? Hier registrieren
              </Link>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                href="/password-reset"
                sx={{
                  color: '#4a5568',
                  textDecoration: 'none',
                  fontSize: '0.8125rem',
                  
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#2d3748',
                  },
                }}
              >
                Passwort vergessen?
              </Link>
            </Box>
          </Box>
        </CardContent>

        {/* Footer */}
        <Box sx={{ 
          p: 3, 
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f7fafc',
          textAlign: 'center'
        }}>
          <Typography variant="caption" sx={{ color: '#718096' }}>
            © 2024 Immobilienverwaltung - immo-automation GmbH
          </Typography>
        </Box>
      </LoginCard>
    </LoginContainer>
  );
};

export default TritonLogin;
